import React from 'react';
import { wtApiCall } from '../../helpers/commons/ApiWrapper';
import { lzNotifyErrorMessageFromResult } from '../../helpers/commons/notifications';
import { CCol, CRow } from '@coreui/react';
import './index.css';
import Highcharts from 'highcharts';
import drilldown from 'highcharts/modules/drilldown.js';
import HighchartsReact from 'highcharts-react-official';
import Loading from 'src/helpers/commons/loader';
import { wtFormatRoundedValue, wtRoundValue } from 'src/helpers/commons/utils';
import { Table, Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector';
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage';
import { ATTENDANCE_CHANGE_APPROVAL_STATUS_PENDING } from 'src/models/attendanceChanges'
import { STATUS_APPROVED, STATUS_JUST_PLACED } from 'src/models/vouchers';
import BaseListing from 'src/BaseListings'
import { currentFullYear, wtGetDateToday } from 'src/helpers/commons/moment';
import Counter from './Counter'; // Import the Counter component
const TYPE_LEAVE = 'Leave';
const TYPE_PRESENT = 'Present';
const TYPE_ABSENT = 'Absent';
const TYPE_WEEKLY_HOLIDAY = 'Weekly Holiday';
const TYPE_PUBLIC_HOLIDAY = 'Public Holiday';
const TYPE_ON_MOVE = 'On Move';
const TYPE_UNPRESENT = 'Unpresented';

const branches = wtGetFromLocalStorage("branches");
const defaultBranchName = wtGetFromLocalStorage("default_branch_name");
const defaultBranchId = wtGetFromLocalStorage("default_branch_id");

class ManagementDashboard extends BaseListing {

    constructor(props) {
        super(props);
        this.state = {
            data: null,
            mainDashboardData: null,
            financeDashboardData: null,
            isLoading: false,
            searchText: '',
            searchedColumn: '',
            branch_id: defaultBranchId,
            branch_name: defaultBranchName,
            from_date: "2020-01-01",
            to_date: wtGetDateToday(),
            asset_class_account_id: 0,
            liability_class_account_id: 0,
            equity_class_account_id: 0,
            income_class_account_id: 0,
            expense_class_account_id: 0,

        };
    }

    componentDidMount() {
        drilldown(Highcharts);
        this.setState({ isLoading: true });
        Promise.all([
            this.loadList(),
            this.getData(),
            this.getFinanceData()
        ]).then(() => {
            // All functions have completed
            this.setState({ isLoading: false });
        });
    }

    loadList(branch_id = this.state.branch_id) {

        return new Promise((resolve) => {
            wtApiCall(
                "hr_dashboards",
                {
                    branch_id: branch_id
                },
                "get",
                (result) => {
                    if (typeof result !== 'undefined') {
                        this.setState({
                            data: result.data
                        });
                    }
                    resolve();
                },
                (result) => {
                    lzNotifyErrorMessageFromResult(result);
                    resolve();
                }
            );
        })
    }

    handleCounterCompletion = () => {
        drilldown(Highcharts);
        this.setState({ isLoading: true });
        Promise.all([
            this.loadList(),
            this.getData(),
            this.getFinanceData()
        ]).then(() => {
            // All functions have completed
            this.setState({ isLoading: false });
        });
    }

    getData() {

        return new Promise((resolve) => {
            wtApiCall(
                'dashboard',
                {},
                'get',
                (response) => {
                    this.setState({ mainDashboardData: response.data });
                    resolve();
                },
                (error) => {
                    lzNotifyErrorMessageFromResult(error)
                    resolve();
                },
            )
        })
    }

    getFinanceData() {
        return new Promise((resolve) => {
            wtApiCall(
                "reports/finance_dashboard",
                {
                    branch_id: (this.state.branch_id === -1) ? 0 : this.state.branch_id
                },
                "get",
                (result) => {
                    if (typeof result !== 'undefined') {
                        this.setState({
                            financeDashboardData: result.data,
                            asset_class_account_id: result.data.asset_class_account_id,
                            liability_class_account_id: result.data.liability_class_account_id,
                            equity_class_account_id: result.data.equity_class_account_id,
                            income_class_account_id: result.data.income_class_account_id,
                            expense_class_account_id: result.data.expense_class_account_id,
                        });
                    }
                    resolve();
                },
                (result) => {
                    lzNotifyErrorMessageFromResult(result);
                    resolve();
                }
            );
        })
    }

    // Column filter input
    getColumnSearchProps = dataIndex => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => this.handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    });

    // Handle search input change
    handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        this.setState({
            searchText: selectedKeys[0],
            searchedColumn: dataIndex,
        });
    };

    // Handle clear search
    handleReset = clearFilters => {
        clearFilters();
        this.setState({ searchText: '' });
    };

    changeBranch(values) {
        this.setState({ branch_id: values.value, branch_name: values.label, isLoading: true }, () =>
            Promise.all([
                this.loadList(values.value === 0 ? -1 : values.value),
                this.getFinanceData()
            ]).then(() => {
                // All functions have completed
                this.setState({ isLoading: false });
            })
        );
    }


    render() {
        const {
            isLoading,
            data,
            mainDashboardData,
            financeDashboardData,
            branch_id,
            branch_name,
            from_date,
            to_date,
            asset_class_account_id,
            liability_class_account_id,
            equity_class_account_id,
            income_class_account_id,
            expense_class_account_id,
        } = this.state;

        if (isLoading) {
            return Loading;
        }


        const employeeFieldsUpcomingBirthdays = [
            {
                key: "profile_picture",
                title: "Picture",
                dataIndex: 'profile_picture',
            },
            {
                key: "name",
                title: "Name",
                dataIndex: 'name',
                sorter: (a, b) => a.name.localeCompare(b.name),
                ...this.getColumnSearchProps('name'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "department",
                title: "Department",
                dataIndex: 'department',
                sorter: (a, b) => a.department.localeCompare(b.department),
                ...this.getColumnSearchProps('department'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "designation",
                title: "Designation",
                dataIndex: 'designation',
                sorter: (a, b) => a.designation.localeCompare(b.designation),
                ...this.getColumnSearchProps('designation'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "cnic",
                title: "CNIC",
                dataIndex: 'cnic',
                sorter: (a, b) => a.cnic.localeCompare(b.cnic),
                ...this.getColumnSearchProps('cnic'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "date_of_birth",
                title: "Date Of Birth",
                dataIndex: 'date_of_birth',
                sorter: (a, b) => a.date_of_birth.localeCompare(b.date_of_birth),
                ...this.getColumnSearchProps('date_of_birth'),
                sortDirections: ['ascend', 'descend'],
            },
        ];

        const employeeFieldsUpcomingAniversaries = [

            {
                key: "name",
                title: "Name",
                dataIndex: 'name',
                sorter: (a, b) => a.name.localeCompare(b.name),
                ...this.getColumnSearchProps('name'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "department",
                title: "Department",
                dataIndex: 'department',
                sorter: (a, b) => a.department.localeCompare(b.department),
                ...this.getColumnSearchProps('department'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "designation",
                title: "Designation",
                dataIndex: 'designation',
                sorter: (a, b) => a.designation.localeCompare(b.designation),
                ...this.getColumnSearchProps('designation'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "cnic",
                title: "CNIC",
                dataIndex: 'cnic',
                sorter: (a, b) => a.cnic.localeCompare(b.cnic),
                ...this.getColumnSearchProps('cnic'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "date_of_joining",
                title: "Date Of Joining",
                dataIndex: 'date_of_joining',
                sorter: (a, b) => a.date_of_joining.localeCompare(b.date_of_joining),
                ...this.getColumnSearchProps('date_of_joining'),
                sortDirections: ['ascend', 'descend'],
            },
        ];

        const employeeFieldsApproachingCnicExpiry = [

            {
                key: "name",
                title: "Name",
                dataIndex: 'name',
                sorter: (a, b) => a.name.localeCompare(b.name),
                ...this.getColumnSearchProps('name'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "department",
                title: "Department",
                dataIndex: 'department',
                sorter: (a, b) => a.department.localeCompare(b.department),
                ...this.getColumnSearchProps('department'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "designation",
                title: "Designation",
                dataIndex: 'designation',
                sorter: (a, b) => a.designation.localeCompare(b.designation),
                ...this.getColumnSearchProps('designation'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "cnic",
                title: "CNIC",
                dataIndex: 'cnic',
                sorter: (a, b) => a.cnic.localeCompare(b.cnic),
                ...this.getColumnSearchProps('cnic'),
                sortDirections: ['ascend', 'descend'],
            },
            {
                key: "cnic_expiry_date",
                title: "CNIC Expiry",
                dataIndex: 'cnic_expiry_date',
                sorter: (a, b) => a.cnic_expiry_date.localeCompare(b.cnic_expiry_date),
                ...this.getColumnSearchProps('cnic_expiry_date'),
                sortDirections: ['ascend', 'descend'],
            },
        ];


        /////////////////////////////////////////////////////

        const genderByDepartmentData = data ? data.gender_distribution_by_department : [];

        const genderByDepartmentChartConfig = {
            chart: {
                type: 'column',
                // height: 300,
            },
            title: {
                text: 'Gender Distribution by Department',
            },
            xAxis: {
                type: 'category',
                categories: genderByDepartmentData.map(entry => entry.department),
            },
            yAxis: {
                title: {
                    text: 'Employee Count',
                },
            },
            plotOptions: {
                series: {
                    stacking: 'normal',
                },
            },
            series: [
                { name: 'Male', data: genderByDepartmentData.map(entry => entry.male) },
                { name: 'Female', data: genderByDepartmentData.map(entry => entry.female) },
            ],
        };

        //Set headcount chart data
        const genderData = {
            chart: {
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
                type: "pie",
                height: 350,
                events: {
                    load() {
                        const chart = this,
                            x = chart.plotSizeX / 2 - chart.plotLeft / 2,
                            y = (chart.plotSizeY / 2 + chart.plotTop) + 10;

                        chart.middleValue = chart.renderer
                            .text((data ? data.head_count : 0), 0, 0)
                            .css({
                                fontSize: "32px",
                            })
                            .add();
                        chart.middleValue.translate(
                            x - chart.middleValue.getBBox().width / 4,
                            y
                        );
                    },
                    render() {
                        const chart = this,
                            x = chart.plotSizeX / 2 - chart.plotLeft / 2,
                            y = (chart.plotSizeY / 2 + chart.plotTop) + 10;

                        if (chart.middleValue) {
                            chart.middleValue.translate(
                                x - chart.middleValue.getBBox().width / 4,
                                y
                            );
                        }
                    },
                },
            },
            title: {
                text: "Headcount",
                align: "center",
                style: {
                    fontWeight: "bold",
                    fontSize: "22px",
                    color: "#333",
                    fontFamily: "sans-serif",
                },
            },
            legend: {
                enabled: true, // Enable/Disable the legend
            },
            plotOptions: {
                pie: {
                    dataLabels: {
                        distance: -18,
                        enabled: true,
                        format: "{y}",
                        style: {
                            fontWeight: "bold",
                            fontSize: "15px",
                            color: "black",
                            textShadow: "0px 0px black",
                        },
                    },
                    startAngle: 0,
                    endAngle: 360,
                    size: "100%",
                    showInLegend: true,
                },
            },
            series: [
                {
                    type: "pie",
                    name: "",
                    innerSize: "60%",
                    colors: ["#FF7C82", "#009CCD"],
                    data: [
                        {
                            name: "Females",
                            y: (data ? data.female_count : 0),
                            dataLabels: {
                                enabled: true,
                            },
                        },
                        {
                            name: "Males",
                            y: (data ? data.male_count : 0),
                            dataLabels: {
                                enabled: true,
                            },
                        },
                    ],
                },
            ]
        };


        ///////////////////////////////////////////////////////


        const statusData = data ? data.job_status_distributions : [];

        const statusChartConfig = {
            chart: {
                type: 'pie',
            },
            title: {
                text: 'Employee Status Distribution',
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                    },
                    innerSize: '50%', // Adjust the size to create a hole in the center (donut chart)
                },
            },
            series: [
                {
                    name: 'Status Distribution',
                    colorByPoint: true,
                    data: statusData,
                },
            ],
        };


        ///////////////////////////////////////////////////////

        const maritalStatusDistributionData = data ? data.marital_status_distributions : [];

        const maritalStatusDistributionChartConfig = {
            chart: {
                type: 'pie',
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
            },
            title: {
                text: 'Marital Status Distribution',
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                    },
                },
            },
            series: [
                {
                    name: 'Marital Status',
                    colorByPoint: true,
                    data: maritalStatusDistributionData,
                },
            ],
        };


        ///////////////////////////////////////////////////////

        const attendanceTypesTodayData = data ? data.attendance_types_today : [];
        const attendanceTypesDrilldown = data ? data.attendance_type_drilldown : [];

        const attendanceTypesTodayDataChartConfig = {
            chart: {
                type: 'pie',
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
                height: 350,
            },
            title: {
                text: 'Attendance Type Distribution',
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                    },
                    // Include drilldown options here

                },
            },
            series: [
                {
                    name: 'No of Employees',
                    colorByPoint: true,
                    data: attendanceTypesTodayData,
                },
            ],
            drilldown: {
                series: attendanceTypesDrilldown,
                plotOptions: {
                    pie: {
                        dataLabels: {
                            format: '<b>{point.name}</b>: {point.y}', // Remove percentage from data labels for drilldown
                        },
                    },
                },
            }
        };
        /////////////////////////////////////////////////////

        const performanceEvaluationStatusData = data ? data.performance_evaluation_status_distribution : [];

        const performanceEvaluationStatusChartConfig = {
            chart: {
                type: 'pie',
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
            },
            title: {
                text: 'Performance Evaluation Distribution',
            },

            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                    },
                },
            },
            series: [
                {
                    name: 'Status',
                    colorByPoint: true,
                    data: performanceEvaluationStatusData,
                },
            ],
        };

        /////////////////////////////////////////////////////

        const employeeDistributionByDepartmentData = data ? data.employee_distribution_by_department : [];
        const employeeDistributionByDepartment = {
            chart: {
                type: 'pie',
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
            },
            title: {
                text: 'Department Strength',
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                    },
                },
            },
            series: [
                {
                    name: 'No. Of Employees',
                    colorByPoint: true,
                    data: employeeDistributionByDepartmentData,
                },
            ],
        };


        /////////////////////////////////////////////////////

        const attendanceDistributionByDepartmentData = data ? data.attendance_distribution_by_department : [];

        const attendanceDistributionByDepartmentChartConfig = {
            chart: {
                type: 'column',
                // height: 300,
            },
            title: {
                text: 'Attendance Distribution by Department',
            },
            xAxis: {
                type: 'category',
                categories: attendanceDistributionByDepartmentData.map(entry => entry.department),
            },
            yAxis: {
                title: {
                    text: 'Employee Count',
                },
            },
            plotOptions: {
                series: {
                    stacking: 'normal',
                },
            },
            series: [
                { name: TYPE_LEAVE, data: attendanceDistributionByDepartmentData.map(entry => entry[TYPE_LEAVE]) },
                { name: TYPE_PRESENT, data: attendanceDistributionByDepartmentData.map(entry => entry[TYPE_PRESENT]) },
                { name: TYPE_ABSENT, data: attendanceDistributionByDepartmentData.map(entry => entry[TYPE_ABSENT]) },
                { name: TYPE_WEEKLY_HOLIDAY, data: attendanceDistributionByDepartmentData.map(entry => entry[TYPE_WEEKLY_HOLIDAY]) },
                { name: TYPE_PUBLIC_HOLIDAY, data: attendanceDistributionByDepartmentData.map(entry => entry[TYPE_PUBLIC_HOLIDAY]) },
                { name: TYPE_ON_MOVE, data: attendanceDistributionByDepartmentData.map(entry => entry[TYPE_ON_MOVE]) },
                { name: TYPE_UNPRESENT, data: attendanceDistributionByDepartmentData.map(entry => entry[TYPE_UNPRESENT]) },
            ],
        };


        return (

            <div className='container-fluid' style={{
                padding: "1em", backgroundColor: "#E5E5E5", minHeight: "100vh", fontFamily: "Helvetica",
                pointerEvents: (isLoading) ? 'none' : 'auto'
            }}>
                {(data !== null && mainDashboardData !== null && financeDashboardData !== null) &&
                    <>
                        <CRow style={{ fontFamily: "sans-serif", fontWeight: "bolder", width: '100%' }}>
                            <CCol md="12">
                                <CRow>
                                    <CCol md="3">
                                        <div className="management-dashboard-parent-box"
                                            style={{ textAlign: "left", color: "#1C6BB8" }}
                                        >
                                            <h4 style={{ fontWeight: 700 }}> Management Dashboard</h4>
                                        </div>
                                    </CCol>

                                    <CCol md="6"></CCol>

                                    <CCol md="3">
                                        <WidgetBranchSelector
                                            value={this.state.branch_id}
                                            displayValue={this.state.branch_name}
                                            branches={branches}
                                            placeholder="Select Branch"
                                            onChange={(values) => this.changeBranch(values)}
                                            showCloudIcon={false}
                                        />
                                    </CCol>
                                </CRow>

                                <CRow>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/employees')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 15, position: "relative", }}
                                                    src={"avatars/dashboard-icons/headcount.png"} alt="headcount.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 70, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        Head Count
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {data.head_count + " Employees"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/reports?fromDate=' + from_date + '&toDate=' + to_date + '&accountId=' + asset_class_account_id + '&voucherStatus=0&branchId=' + branch_id + '&branchName=' + branch_name + '&projectId=0&hideClosingEntries=0#code:account-statement')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 15, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/asset.png"} alt="headcount.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 70, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        Total Assets
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue(financeDashboardData.total_assets, "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/reports?fromDate=' + from_date + '&toDate=' + to_date + '&accountId=' + liability_class_account_id + '&voucherStatus=0&branchId=' + branch_id + '&branchName=' + branch_name + '&projectId=0&hideClosingEntries=0#code:account-statement')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 15, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/liability.png"} alt="average-age.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 70, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        Total Liabilities
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue(financeDashboardData.total_liabilities, "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/reports?fromDate=' + from_date + '&toDate=' + to_date + '&accountId=' + equity_class_account_id + '&voucherStatus=0&branchId=' + branch_id + '&branchName=' + branch_name + '&projectId=0&hideClosingEntries=0#code:account-statement')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/equity.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 70, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        Total Equity
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue(financeDashboardData.total_equity, "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/reports?fromDate=' + from_date + '&toDate=' + to_date + '&accountId=' + income_class_account_id + '&voucherStatus=0&branchId=' + branch_id + '&branchName=' + branch_name + '&projectId=0&hideClosingEntries=0#code:account-statement')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/profit.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 60, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        {"Total Revenue (" + currentFullYear() + ")"}
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue(financeDashboardData.total_income, "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/reports?fromDate=' + from_date + '&toDate=' + to_date + '&accountId=' + expense_class_account_id + '&voucherStatus=0&branchId=' + branch_id + '&branchName=' + branch_name + '&projectId=0&hideClosingEntries=0#code:account-statement')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/expense.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 60, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        {"Total Expense (" + currentFullYear() + ")"}
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue(financeDashboardData.total_expense, "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>
                                </CRow>

                                <CRow>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box">
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 15, position: "relative", }}
                                                    src={"avatars/dashboard-icons/average-age.png"} alt="average-age.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 55, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        Average Employee Age
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtRoundValue(data.average_employee_age, 2) + " Years"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box">
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/dashboard-icons/average-tenure.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 40, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        Average Employee Tenure
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtRoundValue(data.average_employee_tenure, 2) + " Years"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/performance_evaluations?status=pending')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/pending-performance-evaluation.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Pending Performance <br />
                                                        Evaluation
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.totalPendingEvaluation}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>

                                    <CCol md="3">
                                        <div
                                            style={{ height: "fit-content" }}
                                        >
                                            <HighchartsReact highcharts={Highcharts} options={genderData} />
                                        </div>
                                    </CCol>

                                    <CCol md="5">
                                        <div
                                            style={{ height: "fit-content" }}
                                        >
                                            <HighchartsReact highcharts={Highcharts} options={attendanceTypesTodayDataChartConfig} />
                                        </div>
                                    </CCol>

                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/reports?asOf=' + to_date + '&voucherStatus=0&branchId=' + branch_id + '&branchName=' + branch_name + '&projectId=0#code:profit-and-loss')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/net-profit.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        {"Total Net Profit (" + currentFullYear() + ")"}
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue((financeDashboardData.total_income - financeDashboardData.total_expense), "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box">
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/cash-inflow.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 60, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        {"Total Inflows (" + currentFullYear() + ")"}
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue((financeDashboardData.total_inflows), "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box">
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/finance-dashboard-icons/cash-outflow.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 60, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                        {"Total Outflows (" + currentFullYear() + ")"}
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 70, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {wtFormatRoundedValue((financeDashboardData.total_outflows), "Rs. ", true, 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                    </CCol>
                                </CRow>

                                <br />

                                <CRow>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/performance_evaluations?status=pending_recommendation')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/recomend-performance-evaluation.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Pending Recommend <br />Evaluation
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.totalRecommendingEvaluation}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/performance_evaluations?status=pending_approval')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/approval-performance-evaluation.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Pending Approval <br /> Evaluation
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.totalApprovalEvaluation}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/employee_leave_approval')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/leave-approval.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Pending Leave <br />Approval
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.totalPendingLeaves}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>
                                    <CCol md="10">
                                        <div
                                            style={{ height: "fit-content" }}
                                        >
                                            <HighchartsReact highcharts={Highcharts} options={attendanceDistributionByDepartmentChartConfig} />
                                        </div>
                                    </CCol>

                                </CRow>

                                <br />

                                <CRow>
                                    <CCol md="2">
                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/employee_leave_counter_approval')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box" style={{ marginTop: 0 }}>
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/counter-approval.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Pending Counter<br /> Approval
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.totalCounterLeaves}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/attendance-changes', { state: { approvalStatus: ATTENDANCE_CHANGE_APPROVAL_STATUS_PENDING } })}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/attendance-changes.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Pending Attendance<br /> Changes
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.attendanceChangePending}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/employee-attendance-overtime')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/overtime.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Pending Overtime<br /> Approvals
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.overtimeApprovalPending}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {mainDashboardData.myPerformance !== 'N/A' &&
                                            <div className="management-dashboard-parent-box"
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="management-dashboard-child-box">
                                                    <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                        src={"avatars/dashboard-icons/average-tenure.png"} alt="average-tenure.png" />
                                                    <div style={{ position: "relative" }}>
                                                        <span style={{ left: 40, position: "absolute", fontSize: 14, bottom: 22 }}>
                                                            Evaluation Score
                                                        </span>
                                                        <br />
                                                        <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                            {mainDashboardData.myPerformance}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    </CCol>

                                    <CCol md="10">
                                        <div
                                            style={{ height: "fit-content", backgroundColor: "#fff", height: 400 }}
                                        >
                                            <h5 style={{ fontWeight: 600, color: "#1C6BB8", padding: 20 }}> Employees With Upcoming Birthdays</h5>
                                            <Table
                                                dataSource={data.employees_with_upcoming_birthdays}
                                                columns={employeeFieldsUpcomingBirthdays}
                                                pagination={{ defaultPageSize: 5, position: ['bottomCenter'] }}
                                                size="small"
                                                style={{ textAlign: 'center' }}
                                            />
                                        </div>
                                    </CCol>
                                </CRow>

                                <br />

                                <CRow>
                                    <CCol md="2">
                                        {this.hasRights("Approve Vouchers") &&
                                            <div className="management-dashboard-parent-box"
                                                onClick={() => window.open('/vouchers', { state: { status: STATUS_JUST_PLACED } })}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="management-dashboard-child-box">
                                                    <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                        src={"avatars/management-dashboard-icons/voucher-penidng.png"} alt="average-tenure.png" />
                                                    <div style={{ position: "relative" }}>
                                                        <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                            Vouchers Audition <br />Pending
                                                        </span>
                                                        <br />
                                                        <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                            {mainDashboardData.vouchersApprovalPending}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        }

                                        {this.hasRights("Post Vouchers") &&
                                            <div className="management-dashboard-parent-box"
                                                onClick={() => window.open('/vouchers', { state: { status: STATUS_APPROVED } })}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="management-dashboard-child-box">
                                                    <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                        src={"avatars/management-dashboard-icons/voucher-approval.png"} alt="average-tenure.png" />
                                                    <div style={{ position: "relative" }}>
                                                        <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                            Vouchers Approval <br />Pending
                                                        </span>
                                                        <br />
                                                        <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                            {mainDashboardData.vouchersPostingPending}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        }

                                        {this.hasRights("Approve Bank Transactions") &&
                                            <div className="management-dashboard-parent-box"
                                                onClick={() => navigate('/bank_transactions', { state: { status: STATUS_JUST_PLACED } })}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="management-dashboard-child-box">
                                                    <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                        src={"avatars/management-dashboard-icons/bank-transaction-approval.png"} alt="average-tenure.png" />
                                                    <div style={{ position: "relative" }}>
                                                        <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                            Bank Transaction<br /> Audition Pending
                                                        </span>
                                                        <br />
                                                        <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                            {mainDashboardData.bankTransactionsApprovalPending}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    </CCol>

                                    <CCol md="10">
                                        <div
                                            style={{ height: "fit-content", backgroundColor: "#fff", height: 400 }}
                                        >
                                            <h5 style={{ fontWeight: 600, color: "#1C6BB8", padding: 20 }}> Employees Joined Last Three Months</h5>
                                            <Table
                                                dataSource={data.employees_joined_last_three_months}
                                                columns={employeeFieldsUpcomingAniversaries}
                                                pagination={{ defaultPageSize: 5, position: ['bottomCenter'] }}
                                                size="small"
                                                style={{ textAlign: 'center' }}
                                            />
                                        </div>
                                    </CCol>

                                </CRow>

                                <br />

                                <CRow>
                                    <CCol md="2">
                                        {this.hasRights("Post Bank Transactions") &&
                                            <div className="management-dashboard-parent-box"
                                                onClick={() => navigate('/bank_transactions', { state: { status: STATUS_APPROVED } })}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="management-dashboard-child-box">
                                                    <img style={{ height: 30, top: 8, left: 8, position: "relative", marginTop: 0 }}
                                                        src={"avatars/management-dashboard-icons/bank-transaction-approval.png"} alt="average-tenure.png" />
                                                    <div style={{ position: "relative" }}>
                                                        <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                            Bank Transaction <br />Approval Pending
                                                        </span>
                                                        <br />
                                                        <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                            {mainDashboardData.bankTransactionsPostingPending}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        }


                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/employee_absent_count')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/absent.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Total Absents <br />(This Month)
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.TotalApsent}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>


                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/employee_late_coming_count')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/being-late.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Total Late Coming <br />(This Month)
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.TotalLateComing}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="management-dashboard-parent-box"
                                            onClick={() => window.open('/employee_early_going_count')}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div className="management-dashboard-child-box">
                                                <img style={{ height: 30, top: 8, left: 8, position: "relative", }}
                                                    src={"avatars/management-dashboard-icons/early.png"} alt="average-tenure.png" />
                                                <div style={{ position: "relative" }}>
                                                    <span style={{ left: 50, position: "absolute", fontSize: 14, bottom: 18 }}>
                                                        Total Early Going<br /> (This Month)
                                                    </span>
                                                    <br />
                                                    <span style={{ left: 80, top: 1, position: "absolute", fontSize: 14, bottom: 1 }}>
                                                        {mainDashboardData.TotalEarlyGoing}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CCol>

                                    <CCol md="10">
                                        <div
                                            style={{ height: "fit-content", backgroundColor: "#fff", height: 400 }}
                                        >
                                            <h5 style={{ fontWeight: 600, color: "#1C6BB8", padding: 20 }}> Employees With Upcoming Anniversaries</h5>
                                            <Table
                                                dataSource={data.employees_with_upcoming_anniversaries}
                                                columns={employeeFieldsUpcomingAniversaries}
                                                pagination={{ defaultPageSize: 5, position: ['bottomCenter'] }}
                                                size="small"
                                                style={{ textAlign: 'center' }}
                                            />
                                        </div>
                                    </CCol>
                                </CRow>


                                <br />

                                <CRow>
                                    <CCol md="4">
                                        <div
                                            style={{ height: "fit-content" }}
                                        >
                                            <HighchartsReact highcharts={Highcharts} options={maritalStatusDistributionChartConfig} />
                                        </div>

                                    </CCol>

                                    <CCol md="4">
                                        <div
                                            style={{ height: "fit-content" }}
                                        >
                                            <HighchartsReact highcharts={Highcharts} options={performanceEvaluationStatusChartConfig} />
                                        </div>
                                    </CCol>

                                    <CCol md="4">
                                        <div
                                            style={{ height: "fit-content" }}
                                        >
                                            <HighchartsReact highcharts={Highcharts} options={statusChartConfig} />
                                        </div>

                                    </CCol>
                                </CRow>

                                <br />

                                <CRow>

                                    <CCol md="8">
                                        <div
                                            style={{ height: "fit-content", backgroundColor: "#fff", height: 400 }}
                                        >
                                            <h5 style={{ fontWeight: 600, color: "#1C6BB8", padding: 20 }}> Employees Approaching Cnic Expiry</h5>
                                            <Table
                                                dataSource={data.employees_approaching_expiry_cnic}
                                                columns={employeeFieldsApproachingCnicExpiry}
                                                pagination={{ defaultPageSize: 5, position: ['bottomCenter'] }}
                                                size="small"
                                                style={{ textAlign: 'center' }}
                                            />
                                        </div>
                                    </CCol>

                                    <CCol md="4">
                                        <div
                                            style={{ height: "fit-content", marginTop: 10 }}
                                        >
                                            <HighchartsReact highcharts={Highcharts} options={employeeDistributionByDepartment} />
                                        </div>
                                    </CCol>
                                </CRow>
                                <div style={{ textAlign: 'center', marginTop: 5 }}>
                                    <Counter
                                        duration={300}
                                        interval={1000}
                                        onCounterComplete={this.handleCounterCompletion}
                                    />
                                </div>
                            </CCol>
                        </CRow>
                    </>
                }
            </div >
        )
    }
}
export default ManagementDashboard;
